/**
 * countdown-core.js — the date arithmetic of /countdown-to-date, shared by the
 * editor (tools/countdown-to-date.blade.php) and the page a shared link opens
 * (tools/countdown-to-date-view.blade.php). Touches no DOM; pinned by
 * tests/js/countdown-core.test.mjs.
 *
 * Three rules live here, each learned from a defect of the first version:
 *
 * 1. A CALENDAR DATE IS LOCAL. `new Date(y, 0, 1).toISOString().slice(0, 10)`
 *    is the UTC date of local midnight — in every zone east of Greenwich that
 *    is the day before. The «Новый год» preset counted down to 31 December in
 *    Moscow, Berlin, Seoul and Jakarta (6 of our 10 locales). Dates are written
 *    from local fields only: localYmd().
 *
 * 2. «THE EVENT HAS ARRIVED» IS A CROSSING, NOT A STATE. The editor celebrated
 *    whenever the target was in the past — and a date field being typed into
 *    passes through the past on the way: the first digit of the year gives
 *    0002-11-15. 41 % of the sessions that set a countdown logged
 *    `countdown_reached` (RU shard, 60 days to 2026-10-16), confetti and the
 *    chime included, mostly mid-typing. The ticker fires onReached only when a
 *    target it has watched in the future reaches zero.
 *
 * 3. A SHARED MOMENT CARRIES ITS ZONE. «2026-10-28T19:00» with no zone is a
 *    different instant for every reader: a webinar at 19:00 in Moscow ended
 *    four hours early in Novosibirsk. A link carries the author's IANA zone
 *    (`tz`) and the reader counts to the same instant. A holiday preset is the
 *    exception and carries none: New Year is everyone's own midnight.
 */
(function (root, factory) {
    var api = factory();
    if (typeof window !== 'undefined') window.CountdownCore = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
}(this, function () {
    'use strict';

    var DAY = 86400000;
    var THEMES = ['default', 'neon', 'lcd', 'minimal'];

    function pad2(n) { return (n < 10 ? '0' : '') + n; }

    /** YYYY-MM-DD of a Date in the device's own calendar. */
    function localYmd(d) {
        return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
    }

    /** Next local occurrence of month/day at hh:mm (today counts while still ahead). */
    function nextOccurrence(month, day, hh, mm, now) {
        now = now || new Date();
        var y = now.getFullYear();
        var d = new Date(y, month, day, hh || 0, mm || 0, 0, 0);
        if (d.getTime() <= now.getTime()) d = new Date(y + 1, month, day, hh || 0, mm || 0, 0, 0);
        return d;
    }

    /** The device's IANA zone, or null where Intl cannot name it. */
    function localZone() {
        try {
            var z = Intl.DateTimeFormat().resolvedOptions().timeZone;
            return (typeof z === 'string' && z) ? z : null;
        } catch (e) { return null; }
    }

    /** Is this an IANA zone the engine knows? */
    function validZone(tz) {
        if (typeof tz !== 'string' || !tz || tz.length > 64) return false;
        try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; } catch (e) { return false; }
    }

    /** Wall-clock fields of an instant as seen in `tz`. */
    function wallFields(ms, tz) {
        var parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz, hourCycle: 'h23',
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
        }).formatToParts(new Date(ms));
        var f = {};
        for (var i = 0; i < parts.length; i++) f[parts[i].type] = parts[i].value;
        return {
            y: +f.year, mo: +f.month, d: +f.day,
            h: (+f.hour) % 24, mi: +f.minute, s: +f.second
        };
    }

    /** Minutes east of UTC that `tz` keeps at the instant `ms`. */
    function zoneOffsetMin(ms, tz) {
        var w = wallFields(ms, tz);
        var asUtc = Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi, w.s);
        return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60000);
    }

    var DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
    var TIME_RE = /^(\d{2}):(\d{2})$/;

    /**
     * The instant of a wall-clock date and time.
     *   tz given  → that time in that zone (the same moment for every reader);
     *   tz absent → that time on the reading device (a «floating» holiday).
     * Returns epoch ms, or null for anything that is not a real calendar date.
     * Years below 1000 are refused on purpose: no event is counted down to,
     * and they are exactly what a half-typed year looks like.
     */
    function instant(dateStr, timeStr, tz) {
        var dm = DATE_RE.exec(dateStr || '');
        if (!dm) return null;
        var tm = TIME_RE.exec(timeStr || '00:00') || TIME_RE.exec('00:00');
        var y = +dm[1], mo = +dm[2], d = +dm[3], h = +tm[1], mi = +tm[2];
        if (y < 1000 || mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
        if (!tz) {
            var local = new Date(y, mo - 1, d, h, mi, 0, 0);
            if (local.getMonth() !== mo - 1 || local.getDate() !== d) return null;   // 31 June
            return local.getTime();
        }
        if (!validZone(tz)) return null;
        var naive = Date.UTC(y, mo - 1, d, h, mi);
        if (new Date(naive).getUTCDate() !== d) return null;
        // Two passes: the offset at the guess, then at the corrected instant —
        // that settles the days a zone changes its clocks.
        var t = naive - zoneOffsetMin(naive, tz) * 60000;
        t = naive - zoneOffsetMin(t, tz) * 60000;
        return t;
    }

    /** Date and time strings of an instant as the zone `tz` (or the device) writes them. */
    function wallStrings(ms, tz) {
        var w;
        if (tz && validZone(tz)) w = wallFields(ms, tz);
        else {
            var d = new Date(ms);
            w = { y: d.getFullYear(), mo: d.getMonth() + 1, d: d.getDate(), h: d.getHours(), mi: d.getMinutes() };
        }
        return { date: w.y + '-' + pad2(w.mo) + '-' + pad2(w.d), time: pad2(w.h) + ':' + pad2(w.mi) };
    }

    /** «GMT+3», «GMT−4:30» — a zone label that reads the same in every language. */
    function offsetLabel(ms, tz) {
        var m = zoneOffsetMin(ms, tz);
        var sign = m < 0 ? '−' : '+';
        m = Math.abs(m);
        var h = Math.floor(m / 60), r = m % 60;
        return 'GMT' + sign + h + (r ? ':' + pad2(r) : '');
    }

    /** Remaining time split into the four cells. */
    function split(diffMs) {
        var d = Math.max(0, diffMs);
        return {
            days: Math.floor(d / DAY),
            hours: Math.floor((d % DAY) / 3600000),
            minutes: Math.floor((d % 3600000) / 60000),
            seconds: Math.floor((d % 60000) / 1000)
        };
    }

    function addMonths(date, n) {
        var y = date.getFullYear(), m = date.getMonth() + n;
        var targetY = y + Math.floor(m / 12), targetM = ((m % 12) + 12) % 12;
        var last = new Date(targetY, targetM + 1, 0).getDate();
        return new Date(targetY, targetM, Math.min(date.getDate(), last),
            date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
    }

    /**
     * Calendar months, then weeks and days of what is left. The first version
     * took months as 30 days and the days as `total % 7` of the WHOLE span, so
     * 45 days read «1 мес., 2 нед., 3 д.» instead of «… 1 д.».
     */
    function calendarSpan(now, target) {
        if (target <= now) return { months: 0, weeks: 0, days: 0 };
        var months = 0;
        while (addMonths(now, months + 1).getTime() <= target.getTime()) months++;
        var rest = Math.floor((target.getTime() - addMonths(now, months).getTime()) / DAY);
        return { months: months, weeks: Math.floor(rest / 7), days: rest % 7 };
    }

    /** What a link to a countdown says, validated. */
    function readLink(params) {
        var get = function (a, b) { return params.get(a) || (b ? params.get(b) : null) || ''; };
        var raw = get('date', 'd');
        var parts = raw.split('T');
        var date = DATE_RE.test(parts[0] || '') ? parts[0] : '';
        var time = TIME_RE.test((parts[1] || '').slice(0, 5)) ? parts[1].slice(0, 5) : '';
        var tz = get('tz');
        var theme = get('theme');
        return {
            event: get('event', 'e').slice(0, 80),
            date: date,
            time: time,
            tz: validZone(tz) ? tz : '',
            theme: THEMES.indexOf(theme) >= 0 ? theme : ''
        };
    }

    /** Query string of a countdown link (no leading «?»). */
    function linkQuery(c) {
        var q = [];
        var add = function (k, v) { if (v) q.push(k + '=' + encodeURIComponent(v)); };
        add('event', c.event);
        add('date', c.date ? c.date + (c.time && c.time !== '00:00' ? 'T' + c.time : '') : '');
        add('tz', c.tz);
        add('theme', c.theme && c.theme !== 'default' ? c.theme : '');
        return q.join('&');
    }

    /**
     * One-second ticker that knows the difference between «the target is in
     * the past» and «the target has just been reached». onTick(diffMs) runs on
     * every tick; onReached runs once per target, only if this ticker saw that
     * target ahead of the clock first.
     */
    function ticker(opts) {
        var getTarget = opts.getTarget, now = opts.now || Date.now;
        var lastTarget = null, armed = false, timer = null;
        function tick() {
            var t = getTarget();
            if (t !== lastTarget) { lastTarget = t; armed = (t != null && t - now() > 0); }
            var diff = (t == null) ? null : t - now();
            if (opts.onTick) opts.onTick(diff, t);
            if (armed && diff != null && diff <= 0) {
                armed = false;
                if (opts.onReached) opts.onReached(t);
            }
        }
        return {
            tick: tick,
            start: function () { if (timer) clearInterval(timer); tick(); timer = setInterval(tick, 1000); },
            stop: function () { if (timer) clearInterval(timer); timer = null; },
            // A target replaced by the person starts unarmed again.
            reset: function () { lastTarget = undefined; }
        };
    }

    // ── The face (DOM) ──────────────────────────────────────────────────────
    // Everything above is arithmetic and runs in Node; this draws it into the
    // markup of resources/views/tools/partials/countdown-display.blade.php.

    function launchConfetti(canvas, host) {
        if (!canvas || !host || !canvas.getContext) return;
        canvas.hidden = false;
        var rect = host.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
        var ctx = canvas.getContext('2d');
        var colors = ['#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff', '#5f27cd', '#01a3a4', '#f368e0', '#ff5e57', '#0be881'];
        var parts = [];
        for (var i = 0; i < 180; i++) {
            parts.push({
                x: canvas.width * Math.random(), y: -10 - Math.random() * canvas.height * 0.5,
                vx: (Math.random() - 0.5) * 4, vy: Math.random() * 3 + 2,
                size: Math.random() * 6 + 3, color: colors[Math.floor(Math.random() * colors.length)],
                rot: Math.random() * 360, rotV: (Math.random() - 0.5) * 8, life: 1
            });
        }
        (function frame() {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            var alive = false;
            parts.forEach(function (p) {
                if (p.life <= 0) return;
                alive = true;
                p.x += p.vx; p.y += p.vy; p.vy += 0.04; p.vx *= 0.999; p.rot += p.rotV; p.life -= 0.004;
                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rot * Math.PI / 180);
                ctx.globalAlpha = Math.min(p.life * 2, 1);
                ctx.fillStyle = p.color;
                ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
                ctx.restore();
            });
            if (alive) requestAnimationFrame(frame); else canvas.hidden = true;
        })();
    }

    /**
     * Draw a live countdown into the shared face.
     *   opts.getTarget()  epoch ms or null
     *   opts.getTitle()   the event's name ('' → strings.default_title)
     *   opts.fmtDate(ms)  how the target's date line reads
     *   opts.pastText     the line for a target that was ALREADY past when it
     *                     was set or opened (the editor says «the date has
     *                     passed», a shared page says «the event has come»)
     *   opts.strings      {passed, default_title, ext_months, ext_weeks, ext_days}
     *   opts.onReached()  after the live crossing — confetti is drawn here
     * Returns the ticker.
     */
    function face(opts) {
        var $ = function (id) { return document.getElementById(id); };
        var S = opts.strings || {};
        var el = {
            root: $('cd-display'), days: $('cd-days'), hours: $('cd-hours'), mins: $('cd-minutes'), secs: $('cd-seconds'),
            title: $('cd-event-title'), date: $('cd-event-date'), ext: $('cd-extended'), passed: $('cd-passed'), confetti: $('cd-confetti')
        };
        var reachedTarget = null;
        var set = function (node, text) { if (node && node.textContent !== text) node.textContent = text; };
        function cells(a, b, c, d) { set(el.days, a); set(el.hours, b); set(el.mins, c); set(el.secs, d); }

        var tk = ticker({
            getTarget: opts.getTarget,
            onTick: function (diff, t) {
                set(el.title, opts.getTitle() || S.default_title || '');
                if (t == null) {
                    cells('—', '—', '—', '—');
                    set(el.date, '');
                    if (el.passed) el.passed.hidden = true;
                    if (el.ext) el.ext.hidden = true;
                    return;
                }
                set(el.date, opts.fmtDate(t));
                if (diff <= 0) {
                    cells('00', '00', '00', '00');
                    if (el.ext) el.ext.hidden = true;
                    if (el.passed) {
                        set(el.passed, reachedTarget === t ? '🎉 ' + S.passed : (opts.pastText || S.passed));
                        el.passed.hidden = false;
                    }
                    return;
                }
                if (el.passed) el.passed.hidden = true;
                var p = split(diff);
                cells(String(p.days), pad2(p.hours), pad2(p.minutes), pad2(p.seconds));
                if (el.ext) {
                    var line = '';
                    if (p.days >= 14) {
                        var span = calendarSpan(new Date(), new Date(t)), bits = [];
                        if (span.months > 0) bits.push(span.months + ' ' + S.ext_months);
                        if (span.weeks > 0) bits.push(span.weeks + ' ' + S.ext_weeks);
                        if (span.days > 0 && span.months > 0) bits.push(span.days + ' ' + S.ext_days);
                        line = bits.join(', ');
                    }
                    set(el.ext, line);
                    el.ext.hidden = !line;
                }
            },
            onReached: function (t) {
                reachedTarget = t;
                tk.tick();
                launchConfetti(el.confetti, el.root);
                if (opts.onReached) { try { opts.onReached(t); } catch (e) {} }
            }
        });
        return tk;
    }

    return {
        face: face,
        THEMES: THEMES,
        localYmd: localYmd,
        nextOccurrence: nextOccurrence,
        localZone: localZone,
        validZone: validZone,
        zoneOffsetMin: zoneOffsetMin,
        instant: instant,
        wallStrings: wallStrings,
        offsetLabel: offsetLabel,
        split: split,
        addMonths: addMonths,
        calendarSpan: calendarSpan,
        readLink: readLink,
        linkQuery: linkQuery,
        ticker: ticker
    };
}));
